import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import { format, isPast } from "date-fns";
import React from "react";

/**
 * TaskCardBody - Body section of a task card
 *
 * @param {Object} props
 * @param {Object} props.task - Task data
 * @param {boolean} props.isEditing - Whether task is in edit mode
 * @param {Function} props.onChange - Change handler for edit mode
 * @param {Object} props.editedTask - Edited task data
 * @param {Array} props.users - Available users
 * @param {Function} props.onDateChange - Date change handler
 * @param {Function} props.onAssigneeChange - Assignee change handler
 */
const TaskCardBody = ({
  task,
  isEditing,
  onChange,
  editedTask,
  users,
  onDateChange,
  onAssigneeChange,
}) => {
  // Get assignee name from ID
  const getAssigneeName = (id) => {
    if (!id) return "Unassigned";
    const user = users.find((u) => u.id === id);
    return user ? `${user.firstName} ${user.lastName}` : "Unknown";
  };

  // Check if due date has passed
  const isDueDatePassed = (dateString) => {
    if (!dateString) return false;
    return isPast(new Date(dateString));
  };

  return (
    <div className="mb-3">
      {isEditing ? (
        <div className="space-y-2">
          <textarea
            name="description"
            value={editedTask.description}
            onChange={onChange}
            className="w-full p-1 bg-[#262b30] border border-[#4db1e0] rounded text-white text-xs"
            rows={3}
          />

          <div className="flex flex-col space-y-2">
            <div>
              <label className="text-gray-400 text-xs block mb-1">
                Due Date:
              </label>
              <input
                type="date"
                value={editedTask.due_date || ""}
                onChange={onDateChange}
                className="w-full p-1 bg-[#262b30] border border-[#2f3236] rounded text-white text-xs"
                style={{ colorScheme: "dark" }}
              />
            </div>

            <div>
              <label className="text-gray-400 text-xs block mb-1">
                Assignee:
              </label>
              <select
                value={editedTask.assignee_id || ""}
                onChange={onAssigneeChange}
                className="w-full p-1 bg-[#262b30] border border-[#2f3236] rounded text-white text-xs"
              >
                <option value="">Unassigned</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="text-gray-300 text-xs mb-2 whitespace-pre-wrap">
            {task.description?.substring(0, 150)}
            {task.description?.length > 150 ? "..." : ""}
          </p>

          <div className="flex flex-wrap gap-2 text-xs">
            {task.due_date && (
              <div className="text-gray-400 flex items-center">
                Due: {format(new Date(task.due_date), "MMM d, yyyy")}
                {isDueDatePassed(task.due_date) && (
                  <sl-badge variant="danger" pill className="ml-2 text-[10px]">
                    Overdue
                  </sl-badge>
                )}
              </div>
            )}

            <div className="text-gray-400">
              Assignee: {getAssigneeName(task.assignee_id)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskCardBody;
